<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    class="min-w-0 max-w-full"
    :class="{
      'cursor-not-allowed opacity-50': disabled,
    }"
  >
    <Multiselect
      v-model="internalValue"
      v-bind="$attrs"
      :placeholder="placeholder"
      :options="options"
      :disabled="disabled"
      mode="tags"
      :classes="{
        container:
          'relative box-border flex w-full min-w-0 max-w-full items-center cursor-pointer rounded-sm',
        containerOpen: 'ring-2 !ring-ring ring-offset-2',
        wrapper: input({
          class: 'relative !h-auto min-h-8 min-w-0 items-center overflow-hidden py-1 pr-6',
          size: 'sm',
          variant,
        }),
        caret:
          'relative h-full bg-multiselect-caret bg-center bg-no-repeat w-2 h-4 py-px box-content z-10 opacity-40 flex-shrink-0 flex-grow-0 transition-transform transform pointer-events-none opacity-20',
        caretOpen: 'rotate-180 pointer-events-auto',
        tags:
          'flex min-w-0 flex-grow flex-shrink items-center gap-1 self-center mt-0 leading-none ' +
          (internalValue.length > 1 ? 'flex-wrap content-center' : 'flex-nowrap'),
        option:
          'flex items-center justify-start box-border text-left cursor-pointer text-sm leading-snug py-2 px-2',
        optionSelected: 'text-white bg-primary',
        optionPointed: 'text-gray-800 bg-gray-100',
        clear: 'hidden',
        spacer: '',
        tagsSearchWrapper: 'relative mx-1 flex min-w-2 flex-grow flex-shrink self-center',
      }"
    >
      <template #caret="{ handleCaretClick }">
        <div
          class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
          @click="handleCaretClick"
        >
          <ChevronDownIcon class="h-4 w-4 text-primary" />
        </div>
      </template>

      <template #option="{ option }">
        <p>{{ option.label }}</p>
      </template>

      <template #tag="{ option, handleTagRemove, disabled: tagDisabled }">
        <span
          class="inline-flex min-w-0 max-w-full shrink items-center gap-1 rounded-full bg-primary px-2 py-[1px] text-sm text-white"
          :class="{
            disabled: tagDisabled,
            'cursor-not-allowed': tagDisabled,
          }"
        >
          <span class="min-w-0 truncate">{{ option.label }}</span>

          <svg
            v-if="!tagDisabled"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            class="inline-block h-4 w-4 shrink-0 stroke-white"
            @click="handleTagRemove(option, $event)"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </span>
      </template>
    </Multiselect>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import { input } from '@carvia/theme/lib/cva/input.ts';

export interface MultiSelectOptionData {
  label: string;
  value: string | number;
}

const props = defineProps<{
  modelValue?: Array<string | number>;
  options: MultiSelectOptionData[];
  placeholder?: string;
  disabled?: boolean;
  variant?: 'default' | 'ghost' | 'inline';
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Array<string | number>): void;
}>();

const internalValue = computed({
  get: () => props.modelValue ?? [],
  set: (value: Array<string | number>) => emit('update:modelValue', value),
});
</script>
